import { isPointInsidePolygon, ringNesting } from '../polygonWinding';
import { CrossSectionError, CrossSectionPlane } from './crossSectionTypes';
import type { ReadonlyVec3 } from 'gl-matrix';

export type Vec2Mm = readonly [number, number];
export type Vec3Mm = readonly [number, number, number];

const NORMAL_LENGTH_EPSILON = 1e-9;
const PARALLEL_TOLERANCE_DEG = 0.1;
const COINCIDENT_TOLERANCE_MM = 0.05;
/** Within these, a drawn plane and a displayed slice are the same slice: contours on it are shown and extended there, and a line stays on it. */
const SAME_SLICE_TOLERANCE_DEG = 0.5;
const SAME_SLICE_TOLERANCE_MM = 0.5;
const DUPLICATE_VERTEX_EPSILON_MM = 1e-9;

export interface PlaneFrame {
    readonly originMm: Vec3Mm;
    readonly normal: Vec3Mm;
    readonly u: Vec3Mm;
    readonly v: Vec3Mm;
}

export function toVec3Mm(p: ReadonlyVec3): Vec3Mm {
    return [p[0], p[1], p[2]];
}

function dot3(a: Vec3Mm, b: Vec3Mm): number {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function cross3(a: Vec3Mm, b: Vec3Mm): Vec3Mm {
    return [
        a[1] * b[2] - a[2] * b[1],
        a[2] * b[0] - a[0] * b[2],
        a[0] * b[1] - a[1] * b[0],
    ];
}

function sub3(a: Vec3Mm, b: Vec3Mm): Vec3Mm {
    return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function length3(a: Vec3Mm): number {
    return Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2]);
}

function normalise3(a: Vec3Mm, label: string): Vec3Mm {
    const len = length3(a);
    if (!Number.isFinite(len) || len < NORMAL_LENGTH_EPSILON) {
        throw new CrossSectionError(
            'invalid-parameter',
            `${label} must be a non-degenerate direction, got [${a.join(', ')}]`
        );
    }
    return [a[0] / len, a[1] / len, a[2] / len];
}

/** An orthonormal in-plane basis (u, v) with `normal`, u seeded from the axis least aligned with it. */
export function buildPlaneFrame(plane: CrossSectionPlane): PlaneFrame {
    const originMm = toVec3Mm(plane.originMm);
    if (!originMm.every((c) => Number.isFinite(c))) {
        throw new CrossSectionError(
            'invalid-parameter',
            `plane origin must be finite, got [${originMm.join(', ')}]`
        );
    }
    const normal = normalise3(toVec3Mm(plane.normal), 'plane normal');
    let axis = 0;
    for (let i = 1; i < 3; i++) {
        axis = Math.abs(normal[i]) < Math.abs(normal[axis]) ? i : axis;
    }
    const e: Vec3Mm = [+(axis === 0), +(axis === 1), +(axis === 2)];
    const along = dot3(e, normal);
    const u = normalise3(
        [
            e[0] - along * normal[0],
            e[1] - along * normal[1],
            e[2] - along * normal[2],
        ],
        'plane u axis'
    );
    return { originMm, normal, u, v: cross3(normal, u) };
}

export function projectToPlane(frame: PlaneFrame, pMm: Vec3Mm): Vec2Mm {
    const d = sub3(pMm, frame.originMm);
    return [dot3(d, frame.u), dot3(d, frame.v)];
}

export function liftFromPlane(frame: PlaneFrame, uv: Vec2Mm): Vec3Mm {
    const { originMm, u, v } = frame;
    return [
        originMm[0] + uv[0] * u[0] + uv[1] * v[0],
        originMm[1] + uv[0] * u[1] + uv[1] * v[1],
        originMm[2] + uv[0] * u[2] + uv[1] * v[2],
    ];
}

export function planeSignedDistanceMm(frame: PlaneFrame, pMm: Vec3Mm): number {
    return dot3(sub3(pMm, frame.originMm), frame.normal);
}

export function planesAreCoincident(
    a: PlaneFrame,
    b: PlaneFrame,
    toleranceDeg: number = PARALLEL_TOLERANCE_DEG,
    separationToleranceMm: number = COINCIDENT_TOLERANCE_MM
): boolean {
    const sinAngle = length3(cross3(a.normal, b.normal));
    return (
        sinAngle <= Math.sin((toleranceDeg * Math.PI) / 180) &&
        Math.abs(planeSignedDistanceMm(a, b.originMm)) <= separationToleranceMm
    );
}

/** Whether two drawn planes are the same slice to the drawing tools; @throws CrossSectionError `invalid-parameter` for a degenerate plane. */
export function crossSectionPlanesShareSlice(
    a: CrossSectionPlane,
    b: CrossSectionPlane
): boolean {
    return planesAreCoincident(
        buildPlaneFrame(a),
        buildPlaneFrame(b),
        SAME_SLICE_TOLERANCE_DEG,
        SAME_SLICE_TOLERANCE_MM
    );
}

function pointSegmentDistance(p: Vec2Mm, a: Vec2Mm, b: Vec2Mm): number {
    const abx = b[0] - a[0];
    const aby = b[1] - a[1];
    const apx = p[0] - a[0];
    const apy = p[1] - a[1];
    const lenSq = abx * abx + aby * aby;
    const t =
        lenSq > 0
            ? Math.min(1, Math.max(0, (apx * abx + apy * aby) / lenSq))
            : 0;
    const dx = apx - t * abx;
    const dy = apy - t * aby;
    return Math.sqrt(dx * dx + dy * dy);
}

function isWithinVertexEpsilon(a: Vec2Mm, b: Vec2Mm): boolean {
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];
    const eps = DUPLICATE_VERTEX_EPSILON_MM;
    return dx * dx + dy * dy < eps * eps;
}

/** The polygon without consecutive duplicate vertices or a repeated closing vertex. */
export function canonicalisePolygon(
    polygon: readonly Vec2Mm[]
): readonly Vec2Mm[] {
    const out: Vec2Mm[] = [];
    for (const vertex of polygon) {
        const last = out[out.length - 1];
        if (!last || !isWithinVertexEpsilon(vertex, last)) {
            out.push(vertex);
        }
    }
    while (
        out.length > 1 &&
        isWithinVertexEpsilon(out[0], out[out.length - 1])
    ) {
        out.pop();
    }
    return out;
}

/** Signed distance to the union of canonical rings: negative inside any of them. */
export function polygonSetSdf2d(
    polygons: readonly (readonly Vec2Mm[])[],
    p: Vec2Mm
): number {
    let best = Infinity;
    for (const ring of polygons) {
        let edge = Infinity;
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            edge = Math.min(edge, pointSegmentDistance(p, ring[j], ring[i]));
        }
        best = Math.min(best, isPointInsidePolygon(p, ring) ? -edge : edge);
    }
    return best;
}

/** `inside` when a contour lies within another on its plane, `around` when it encloses it. */
export type ContourNesting = 'inside' | 'around';

/** How `contourMm` nests with `othersMm` on `plane` as `ringNesting` defines it, `inside` first; @throws CrossSectionError `invalid-parameter` for a degenerate plane. */
export function contourNestingOnPlane(
    plane: CrossSectionPlane,
    contourMm: readonly ReadonlyVec3[],
    othersMm: readonly (readonly ReadonlyVec3[])[]
): ContourNesting | null {
    const frame = buildPlaneFrame(plane);
    const inPlane = (contour: readonly ReadonlyVec3[]): Vec2Mm[] =>
        contour.map((p) => projectToPlane(frame, toVec3Mm(p)));
    const ring = inPlane(contourMm);
    let around = false;
    for (const other of othersMm) {
        const { depth } = ringNesting([ring, inPlane(other)]);
        if (depth[0] > 0) {
            return 'inside';
        }
        around ||= depth[1] > 0;
    }
    return around ? 'around' : null;
}

/** A vertex where a ring turns: its arc length and signed turn in radians (−π to π). */
export interface RingCorner {
    readonly arcMm: number;
    readonly turnRad: number;
}

export interface ClosedCurve2d {
    readonly perimeterMm: number;
    /** The point at arc length `t`, wrapping around the ring. */
    at(t: number): Vec2Mm;
    /** The vertices where the ring turns by more than `maxTurnDeg`, in ring order. */
    corners(maxTurnDeg: number): RingCorner[];
}

export function parameteriseClosed(polygon: readonly Vec2Mm[]): ClosedCurve2d {
    const ring = canonicalisePolygon(polygon);
    const n = ring.length;
    if (n < 3) {
        throw new CrossSectionError(
            'too-few-vertices',
            `a cross-section polygon needs at least 3 distinct vertices, got ${n}`
        );
    }
    const cumulative = new Float64Array(n + 1);
    for (let i = 0; i < n; i++) {
        const [a, b] = [ring[i], ring[(i + 1) % n]];
        const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
        cumulative[i + 1] = cumulative[i] + Math.sqrt(dx * dx + dy * dy);
    }
    const perimeterMm = cumulative[n];
    return {
        perimeterMm,
        at(t: number): Vec2Mm {
            let s = t % perimeterMm;
            s = s < 0 ? s + perimeterMm : s;
            let [lo, hi] = [0, n];
            while (lo + 1 < hi) {
                const mid = (lo + hi) >> 1;
                [lo, hi] = cumulative[mid] <= s ? [mid, hi] : [lo, mid];
            }
            const [a, b] = [ring[lo], ring[(lo + 1) % n]];
            const edge = cumulative[lo + 1] - cumulative[lo];
            const f = edge > 0 ? (s - cumulative[lo]) / edge : 0;
            return [a[0] + f * (b[0] - a[0]), a[1] + f * (b[1] - a[1])];
        },
        corners(maxTurnDeg: number): RingCorner[] {
            const threshold = (maxTurnDeg * Math.PI) / 180;
            const out: RingCorner[] = [];
            for (let i = 0; i < n; i++) {
                const prev = ring[(i - 1 + n) % n];
                const [here, next] = [ring[i], ring[(i + 1) % n]];
                let turn =
                    Math.atan2(next[1] - here[1], next[0] - here[0]) -
                    Math.atan2(here[1] - prev[1], here[0] - prev[0]);
                while (turn > Math.PI) {
                    turn -= 2 * Math.PI;
                }
                while (turn < -Math.PI) {
                    turn += 2 * Math.PI;
                }
                if (Math.abs(turn) > threshold) {
                    out.push({ arcMm: cumulative[i], turnRad: turn });
                }
            }
            return out;
        },
    };
}

/** The ring at max(3, round(perimeter / `spacingMm`)) evenly spaced points from its first vertex; @throws CrossSectionError `invalid-parameter`, `too-few-vertices`. */
export function resampleClosedPolygonUniform(
    polygon: readonly Vec2Mm[],
    spacingMm: number
): Vec2Mm[] {
    if (!Number.isFinite(spacingMm) || spacingMm <= 0) {
        throw new CrossSectionError(
            'invalid-parameter',
            `resampleClosedPolygonUniform: spacingMm must be positive, got ${spacingMm}`
        );
    }
    const curve = parameteriseClosed(polygon);
    const count = Math.max(3, Math.round(curve.perimeterMm / spacingMm));
    const step = curve.perimeterMm / count;
    return Array.from({ length: count }, (_, i) => curve.at(i * step));
}

/**
 * At most `maxSamples` points in ring order: the sharpest corners that fit the budget, then `sampleCount` even samples yielding to corners within a quarter step.
 * @throws CrossSectionError `invalid-parameter` for a bad count, `too-few-vertices` for fewer than 3 vertices.
 */
export function resampleClosedPolygon(
    polygon: readonly Vec2Mm[],
    sampleCount: number,
    maxTurnDeg: number,
    maxSamples: number
): Vec2Mm[] {
    if (!Number.isFinite(sampleCount) || sampleCount < 3) {
        throw new CrossSectionError(
            'invalid-parameter',
            `resampleClosedPolygon: sampleCount must be at least 3, got ${sampleCount}`
        );
    }
    if (!(maxSamples >= sampleCount)) {
        throw new CrossSectionError(
            'invalid-parameter',
            `resampleClosedPolygon: maxSamples (${maxSamples}) must be at least sampleCount (${sampleCount})`
        );
    }
    const curve = parameteriseClosed(polygon);
    const step = curve.perimeterMm / sampleCount;
    const kept = curve
        .corners(maxTurnDeg)
        .sort(
            (a, b) =>
                Math.abs(b.turnRad) - Math.abs(a.turnRad) || a.arcMm - b.arcMm
        )
        .slice(0, Math.floor(maxSamples - sampleCount))
        .map((corner) => corner.arcMm);
    for (let i = 0; i < sampleCount; i++) {
        const t = i * step;
        const clash = kept.some((k) => {
            const d = Math.abs(k - t);
            return Math.min(d, curve.perimeterMm - d) < step / 4;
        });
        if (!clash) {
            kept.push(t);
        }
    }
    return kept.sort((a, b) => a - b).map((t) => curve.at(t));
}
